import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  OWL_DATE_TIME_FORMATS,
  OWL_DATE_TIME_LOCALE,
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
} from '@danielmoncada/angular-datetime-picker';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { ProgressSpinnerComponent } from 'src/app/shared/components/progress-spinner/progress-spinner.component';
import { ApifonConfigurationFacadeService } from '../modules/administration/integration-management/services/apifon-configuration.facade.service';
import { EliquaCXConfigurationFacadeService } from '../modules/administration/integration-management/services/eliqua-cx-configuration.facade.service';
import { EnboxConfigurationFacadeService } from '../modules/administration/integration-management/services/enbox-configuration.facade.service';
import { IntegrationManagementActionsFacadeService } from '../modules/administration/integration-management/services/integration-management-actions.facade.service';
import { IMCreateNewFacadeService } from '../modules/administration/integration-management/services/integration-management-create-new.facade.service';
import { NeverbounceConfigurationFacadeService } from '../modules/administration/integration-management/services/neverbounce-configuration.facade.service';
import { QcMessageAppConfigurationFacadeService } from '../modules/administration/integration-management/services/qc-message-app-configuration.facade.service';
import { SesConfigurationFacadeService } from '../modules/administration/integration-management/services/ses-configuration.facade.service';
import { SearchFlowsFacadeService } from '../modules/automation-center/flows/services/search-flows.facade.service';
import { CampaignManagementFacadeService } from '../modules/campaign-management/campaign-executions/services/search.facade.service';
import { ListsFacadeService } from '../modules/campaign-management/campaigns/services/lists.facade.service';
import { PreviewListFacadeService } from '../modules/campaign-management/campaigns/services/preview-list.facade.service';
import { AssignCheckbookFacadeService } from '../modules/campaign-management/checkbooks/services/assign-checkbook.facade.service';
import { CheckbookGroupTemplateFacadeService } from '../modules/campaign-management/checkbooks/services/checkbook-group-template.facade.service';
import { SearchCheckbooksFacadeService } from '../modules/campaign-management/checkbooks/services/search-checkbooks.facade.service';
import { SearchGroupTemplatesFacadeService } from '../modules/campaign-management/checkbooks/services/search-group-templates.facade.service';
import { SidenavFacadeService } from '../modules/campaign-management/checkbooks/services/sidenav.facade.service';
import { AllCustomersFacadeService } from '../modules/customer-data/customers/services/all-customers.facade.service';
import { MembersSearchFacadeService } from '../modules/customer-data/customers/services/members-search.facade.service';
import { SharedFacadeService } from '../modules/customer-data/customers/services/shared.facade.service';
import { InvoicesFacadeService } from '../modules/customer-data/invoices/services/invoices.facade.service';
import { TicketsSearchFacadeService } from '../modules/customer-data/tickets/services/tickets-search.facade.service';
import { StoreSearchFacadeService } from '../modules/data-customization/store-management/services/search.facade.service';
import { EditDateFacadeService } from '../modules/data-customization/tag-management/services/edit-date.facade.service';
import { UpdateTagFacadeService } from '../modules/data-customization/tag-management/services/update-tag.facade.service';
import { CreateNewFacadeService as RedeemRulesCreateNewFacadeService } from '../modules/loyalty-model/redeem-rules/services/create-new.facade.service';
import { RedeemRulesSearchFacadeService } from '../modules/loyalty-model/redeem-rules/services/search.facade.service';
import { SchemasSearchFacadeService } from '../modules/loyalty-model/schemas/services/search.facade.service';
import { MerchantSummaryFacadeService } from '../modules/merchant/services/summary.facade.service';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { GridComponent } from './components/grid/grid.component';
import { LockedOverlayComponent } from './components/locked-overlay/locked-overlay.component';
import { NotificationsComponent } from './components/notifications/notifications.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { ProgressBarComponent } from './components/progress-bar/progress-bar.component';
import { TableSkeletonComponent } from './components/table-skeleton/table-skeleton.component';
import { TriStateCheckboxComponent } from './components/tri-state-checkbox/tri-state-checkbox.component';
import { EliquaEffects } from './effects/eliqua.effects';
import { GridEffects } from './effects/grid.effects';
import { LiteralsEffects } from './effects/literals.effects';
import { NotificationEffects } from './effects/notification.effects';
import { PaginatorEffects } from './effects/paginator.effects';
import { RolesEffects } from './effects/roles.effects';
import { AvatarColorPipe } from './pipes/avatar-color.pipe';
import { RemoveUnderscoresPipe } from './pipes/remove-underscores.pipe';
import { TargetGroupPipe } from './pipes/target-groups.pipe';
import { featureSharedName, moduleSharedReducers } from './reducers';

const OWL_NATIVE_FORMATS = {
  fullPickerInput: {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  },
  datePickerInput: { year: 'numeric', month: 'numeric', day: 'numeric' },
  timePickerInput: { hour: 'numeric', minute: 'numeric' },
  monthYearLabel: { year: 'numeric', month: 'short' },
  dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
  monthYearA11yLabel: { year: 'numeric', month: 'long' },
};

@NgModule({
  declarations: [
    GridComponent,
    PaginatorComponent,
    NotificationsComponent,
    ConfirmDialogComponent,
    TriStateCheckboxComponent,
    LockedOverlayComponent,
    ProgressBarComponent,
    ProgressSpinnerComponent,
    TableSkeletonComponent,
    AvatarColorPipe,
    BreadcrumbComponent,
    RemoveUnderscoresPipe,
    TargetGroupPipe,
  ],
  imports: [
    CommonModule,
    RouterModule,
    ClipboardModule,
    MatMenuModule,
    MatFormFieldModule,
    MatBadgeModule,
    MatDialogModule,
    MatInputModule,
    MatDividerModule,
    MatSelectModule,
    MatExpansionModule,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatChipsModule,
    MatPaginatorModule,
    MatButtonToggleModule,
    MatTabsModule,
    MatSortModule,
    MatGridListModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatListModule,
    MatSidenavModule,
    MatToolbarModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    MatStepperModule,
    MatSliderModule,
    StoreModule.forFeature(featureSharedName, moduleSharedReducers),
    EffectsModule.forFeature([
      LiteralsEffects,
      RolesEffects,
      GridEffects,
      PaginatorEffects,
      NotificationEffects,
      EliquaEffects,
    ]),
  ],
  exports: [
    GridComponent,
    ClipboardModule,
    MatMenuModule,
    MatFormFieldModule,
    MatBadgeModule,
    MatDialogModule,
    MatInputModule,
    MatDividerModule,
    MatSelectModule,
    MatExpansionModule,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatChipsModule,
    MatPaginatorModule,
    MatButtonToggleModule,
    MatTabsModule,
    MatSortModule,
    MatGridListModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatListModule,
    MatSidenavModule,
    MatToolbarModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    MatStepperModule,
    MatSliderModule,
    TriStateCheckboxComponent,
    LockedOverlayComponent,
    ProgressSpinnerComponent,
    ProgressBarComponent,
    TableSkeletonComponent,
    AvatarColorPipe,
    BreadcrumbComponent,
    RemoveUnderscoresPipe,
    TargetGroupPipe,
  ],
  providers: [
    { provide: MAT_DIALOG_DATA, useValue: {} },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: OWL_DATE_TIME_FORMATS, useValue: OWL_NATIVE_FORMATS },
    { provide: OWL_DATE_TIME_LOCALE, useValue: 'en-GB' },
    MerchantSummaryFacadeService,
    IntegrationManagementActionsFacadeService,
    EnboxConfigurationFacadeService,
    NeverbounceConfigurationFacadeService,
    ApifonConfigurationFacadeService,
    IMCreateNewFacadeService,
    UpdateTagFacadeService,
    EditDateFacadeService,
    QcMessageAppConfigurationFacadeService,
    SesConfigurationFacadeService,
    EliquaCXConfigurationFacadeService,
    SearchCheckbooksFacadeService,
    SearchGroupTemplatesFacadeService,
    SidenavFacadeService,
    AssignCheckbookFacadeService,
    CheckbookGroupTemplateFacadeService,
    ListsFacadeService,
    PreviewListFacadeService,
    TicketsSearchFacadeService,
    SearchFlowsFacadeService,
    StoreSearchFacadeService,
    MembersSearchFacadeService,
    AllCustomersFacadeService,
    SharedFacadeService,
    CampaignManagementFacadeService,
    SchemasSearchFacadeService,
    RedeemRulesSearchFacadeService,
    RedeemRulesCreateNewFacadeService,
    InvoicesFacadeService,
  ],
})
export class SharedModule {}
